// SAL-3128: scenario-level private instructions for the AI buyer.
//
// A persona is a durable human; a scenario is one moment in that human's life.
// Before this, the only place to say "how does this buyer behave in THIS
// scenario" was the persona's runtime prompt, which is shared by every scenario
// the persona appears in -- so a cold-call brush-off sequence written onto a
// persona followed that person into discovery calls and pitches too. The
// directive holds the encounter; the persona prompt holds the person.
//
// Always optional. A scenario with no directive composes exactly the prompt it
// composed before this existed.
//
// It is one plain nullable column on all six scenario models, read like any
// other. It is admin-authored colour for the simulated buyer, not a secret:
// like `onboardingAccommodation` beside it, it travels in the scenario payloads
// the simulator already fetches. What keeps it out of a learner's way is that
// nothing learner-facing renders it and only a studio author can write it, both
// of which personaDirectiveIsolation.test.js holds. Do not put anything
// genuinely confidential in it. See docs/scenario-field-audiences.md.

import { getScenarioType } from "@/lib/role-plays/scenario-types";

const MAX_DIRECTIVE_LENGTH = 12000;

/**
 * Who may author a persona directive: studio admins and superUsers.
 *
 * Lives here, with the rest of the field's value contract, rather than in
 * persona-directive-access.js -- that module imports prisma, and the scenario
 * editor is a client component. Importing the predicate from there pulled `pg`
 * into the browser bundle and failed the build.
 */
export function canAuthorPersonaDirective(user) {
  return user?.role === "admin" || user?.superUser === true;
}

// Every spelling a scenario type reaches this module under: simulator URL
// segments ("objections", "cold-call", "plays"), MCP tool args ("outbound"),
// and the admin editor's formType ("coldcall").
const TYPE_ALIASES = {
  objection: "objection",
  objections: "objection",
  pitch: "pitch",
  pitches: "pitch",
  discovery: "discovery",
  discoveries: "discovery",
  coldcall: "coldcall",
  "cold-call": "coldcall",
  outbound: "coldcall",
  inbound: "coldcall",
  management: "management",
  "management-practice": "management",
  play: "play",
  plays: "play",
};

export function normalizePersonaDirectiveType(value) {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
  return TYPE_ALIASES[key] || null;
}

/** Does this scenario type carry a persona directive at all? */
export function supportsPersonaDirective(scenarioType) {
  return normalizePersonaDirectiveType(scenarioType) !== null;
}

/** The Prisma delegate that carries the column, from the one scenario-type table. */
export function personaDirectiveModel(scenarioType) {
  const type = normalizePersonaDirectiveType(scenarioType);
  return type ? getScenarioType(type)?.scenarioModel ?? null : null;
}

/**
 * Normalize a directive written by an author, an MCP tool or the editor.
 *
 * @returns {string|null} Trimmed text, or null for "no directive" -- which is
 *   what a blank value means, so the column has one representation for absent.
 * @throws {TypeError|RangeError} On a non-string, non-null value or an
 *   over-long one. Never on a blank value: blank is always a valid answer.
 */
export function normalizePersonaDirective(value) {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string") {
    throw new TypeError("Persona directive must be text or null.");
  }
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_DIRECTIVE_LENGTH) {
    throw new RangeError(
      `Persona directive must be at most ${MAX_DIRECTIVE_LENGTH.toLocaleString()} characters.`,
    );
  }
  return trimmed;
}

/**
 * The column write for a create or update: `{}` when the caller did not supply
 * the field at all (leave it alone), otherwise the normalized value, with a
 * blank clearing it to null.
 */
export function personaDirectiveData(value) {
  if (value === undefined) return {};
  return { personaDirective: normalizePersonaDirective(value) };
}

export { MAX_DIRECTIVE_LENGTH };
